import React, { useState, useEffect } from 'react';
import Modal from '../../../../components/Modal';
import Button from '../../../../components/Button';
import TextField from '../../../../components/TextField';
import { FiCalendar, FiMessageSquare } from 'react-icons/fi';
import Icon from '../../../../components/Icon';

type DeactivationModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (reason: string, date: string) => void;
    psoName?: string;
};

export default function DeactivationModal({ isOpen, onClose, onConfirm, psoName }: DeactivationModalProps) {
    const [reason, setReason] = useState('');
    const [date, setDate] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        if (isOpen) {
            setReason('');
            setDate(new Date().toISOString().split('T')[0]); // Default to today
            setErrors({});
        }
    }, [isOpen]);

    const validate = () => {
        const newErrors: Record<string, string> = {};
        if (!reason.trim()) {
            newErrors.reason = 'Deactivation reason is required.';
        }
        if (!date) {
            newErrors.date = 'Deactivation date is required.';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (validate()) {
            onConfirm(reason, date);
        }
    };

    const handleClose = () => {
        setReason('');
        setDate('');
        setErrors({});
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title={`Deactivate PSO: ${psoName || ''}`} size="sm">
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                    <label className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                        Reason for Deactivation <span className="text-red-500">*</span>
                    </label>
                    <textarea
                        value={reason}
                        onChange={(e) => {
                            setReason(e.target.value);
                            if (errors.reason) {
                                setErrors(prev => ({ ...prev, reason: '' }));
                            }
                        }}
                        rows={3}
                        placeholder="Enter reason for deactivation"
                        className={`w-full rounded-xl border py-3 px-4 text-sm outline-none transition bg-white text-gray-900 placeholder:text-gray-400 border-gray-300 shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600 ${errors.reason ? 'border-red-500' : ''
                            }`}
                    />
                    {errors.reason && (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.reason}</p>
                    )}
                </div>
                <div>
                    <label className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                        Deactivation Date <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="date"
                        value={date}
                        onChange={(e) => {
                            setDate(e.target.value);
                            if (errors.date) {
                                setErrors(prev => ({ ...prev, date: '' }));
                            }
                        }}
                        max={new Date().toISOString().split('T')[0]}
                        className={`w-full rounded-xl border py-3 px-4 text-sm outline-none transition bg-white text-gray-900 border-gray-300 shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600 ${errors.date ? 'border-red-500' : ''
                            }`}
                    />
                    {errors.date && (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.date}</p>
                    )}
                </div>
                <div className="flex justify-end gap-3 mt-6">
                    <Button variant="secondary" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        className="bg-red-600 text-white hover:bg-red-700 focus:ring-red-500"
                    >
                        Deactivate
                    </Button>
                </div>
            </form>
        </Modal>
    );
}

